import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Fade,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import SettingsIcon from "@mui/icons-material/Settings";
import InventoryIcon from "@mui/icons-material/Inventory";
import InfoIcon from "@mui/icons-material/Info";
import ClearIcon from "@mui/icons-material/Clear";
import "./ShiftDetails.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ManualEnter() {
  const theme = useTheme();

  const [reason, setReason] = useState("");
  const [jobNumber, setJobNumber] = useState("");
  const [customer, setCustomer] = useState("");
  const [operation, setOperation] = useState("");
  const [product, setProduct] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const navigate = useNavigate();
  const location = useLocation();
  const username = location.state?.username || sessionStorage.getItem("username");
  const employeeName =
    location.state?.employeeName || sessionStorage.getItem("employeeName");

  useEffect(() => {
    if (location.state?.username) {
      sessionStorage.setItem("username", location.state.username);
    }
    if (location.state?.employeeName) {
      sessionStorage.setItem("employeeName", location.state.employeeName);
    }
  }, [location.state]);

  const showNotification = (message, severity = "info") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };
    if (value.trim() === "") {
      newErrors[fieldName] =
        fieldName.charAt(0).toUpperCase() + fieldName.slice(1) + " is required";
    } else {
      delete newErrors[fieldName];
    }
    setErrors(newErrors);
  };

  const formatJobNumber = (value) => {
    // Allow only alphanumerics; format NN-NN-XXXX
    const cleanValue = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    let formatted = "";
    let count = 0;

    for (let i = 0; i < cleanValue.length && count < 10; i++) {
      const char = cleanValue[i];

      if (count < 4) {
        if (/\d/.test(char)) {
          formatted += char;
          count++;
          if (count === 2 || count === 4) formatted += "-";
        }
      } else if (count < 6) {
        if (/\d/.test(char)) {
          formatted += char;
          count++;
        }
      } else {
        if (/[A-Z0-9]/.test(char)) {
          formatted += char;
          count++;
        }
      }
    }

    return formatted;
  };

  const handleJobNumberChange = (e) => {
    const inputValue = e.target.value;
    const formattedValue = formatJobNumber(inputValue);
    setJobNumber(formattedValue);
    validateField("jobNumber", formattedValue);
  };

  const clearJobNumber = () => {
    setJobNumber("");
    validateField("jobNumber", "");
  };

  const handleConfirm = async () => {
    const newErrors = {};

    const fields = [
      { name: "reason", value: reason, label: "Reason" },
      { name: "jobNumber", value: jobNumber, label: "Job Number" },
      { name: "customer", value: customer, label: "Customer Name" },
      { name: "operation", value: operation, label: "Operation Code" },
      { name: "product", value: product, label: "Product" },
    ];

    fields.forEach((field) => {
      if (field.value.trim() === "") {
        newErrors[field.name] = `${field.label} is required`;
      }
    });

    if (jobNumber && jobNumber.length < 7) {
      newErrors.jobNumber = "Job number must follow the pattern: NN-NN-XXXX";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/getMachineCode?username=${username}`
        );
        const { machineCode } = response.data;

        const time = new Date();
        const formattedStartedTime = time.toISOString().slice(0, 19).replace("T", " ");

        if (machineCode) {
          await sendDataToBackend(formattedStartedTime, machineCode);
        } else {
          showNotification(
            "Machine code not found. Please contact administrator.",
            "error"
          );
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching machine code:", error);
        showNotification(
          "Failed to fetch machine information. Please try again.",
          "error"
        );
        setIsLoading(false);
      }
    } else {
      showNotification("Please fill in all required fields correctly.", "warning");
    }
  };

  const sendDataToBackend = async (formattedStartedTime, machineCode) => {
    const data = {
      jobNumber,
      customer,
      operation,
      product,
      username,
      machineCode,
      startedTime: formattedStartedTime,
      reason,
      employeeName,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/Jobstart`, data);

      if (response.status === 200) {
        const { JobStartedTime: originalJobStartedTime } = response.data;

        const jobStartedDate = new Date(originalJobStartedTime);
        jobStartedDate.setHours(jobStartedDate.getHours() - 5);
        jobStartedDate.setMinutes(jobStartedDate.getMinutes() - 30);
        const adjustedJobStartedTime = jobStartedDate.toISOString();

        showNotification("Job started successfully! Redirecting...", "success");

        setTimeout(() => {
          navigate("/JobOperation", {
            state: {
              reason,
              jobNumber,
              customer,
              operation,
              product,
              startedTime: adjustedJobStartedTime,
              username,
              employeeName,
              machineCode,
              plannedQty: "",
              OperationalTime: "",
              SetupAndOpeTime: "",
              SetupTime: "",
            },
          });
        }, 1500);
      } else {
        showNotification("Failed to start job. Please try again.", "error");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error inserting operation log:", error);
      showNotification(
        "Network error. Please check your connection and try again.",
        "error"
      );
      setIsLoading(false);
    }
  };

  const backToJobSelection = () => {
    navigate("/JobProcessSelection", { state: { username } });
  };

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, document.title, window.location.href);
      alert(
        "You can't go back from this window-කණගාටුයි! ඔබට මෙම පේජ් එකෙන් පසුපසට යා නොහැක."
      );
    };

    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, document.title, window.location.href);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const formFields = [
    {
      id: "reason",
      label: "Reason",
      value: reason,
      setValue: setReason,
      icon: <InfoIcon />,
      placeholder: "Enter the reason for manual job entry",
      maxLength: 50,
      isJobNumber: false,
    },
    {
      id: "jobNumber",
      label: "Job Number",
      value: jobNumber,
      setValue: setJobNumber,
      icon: <WorkIcon />,
      placeholder: "Enter job number (e.g., 11-22-1234)",
      maxLength: 11,
      isJobNumber: true,
    },
    {
      id: "customer",
      label: "Customer",
      value: customer,
      setValue: setCustomer,
      icon: <BusinessIcon />,
      placeholder: "Enter customer name",
      maxLength: 50,
      isJobNumber: false,
    },
    {
      id: "operation",
      label: "Operation",
      value: operation,
      setValue: setOperation,
      icon: <SettingsIcon />,
      placeholder: "Enter operation code",
      maxLength: 50,
      isJobNumber: false,
    },
    {
      id: "product",
      label: "Product",
      value: product,
      setValue: setProduct,
      icon: <InventoryIcon />,
      placeholder: "Enter product name",
      maxLength: 50,
      isJobNumber: false,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: 1, sm: 2 },
        "@media (max-width: 1000px) and (max-height: 600px)": {
          minHeight: "600px",
          padding: 1,
          alignItems: "flex-start",
          paddingTop: 2,
        },
      }}
    >
      {/* Alert Notification */}
      <Fade in={showAlert}>
        <Alert
          severity={alertSeverity}
          sx={{
            position: "fixed",
            top: { xs: 10, sm: 20 },
            right: { xs: 10, sm: 20 },
            zIndex: 2000,
            minWidth: { xs: 250, sm: 300 },
            boxShadow: 3,
            "@media (max-width: 1000px) and (max-height: 600px)": {
              top: 5,
              right: 5,
              minWidth: 200,
              fontSize: "0.8rem",
            },
          }}
          onClose={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Fade>

      <Card
        elevation={12}
        sx={{
          borderRadius: { xs: 2, sm: 4 },
          maxWidth: { xs: "100%", sm: 600 },
          width: "100%",
          background: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: "blur(20px)",
          border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          overflow: "visible",
          "@media (max-width: 1000px) and (max-height: 600px)": {
            maxWidth: "95%",
            borderRadius: 2,
            maxHeight: "95vh",
            overflowY: "auto",
          },
        }}
      >
        {/* Loading Progress Bar */}
        {isLoading && (
          <LinearProgress
            sx={{
              borderRadius: "16px 16px 0 0",
              height: 4,
            }}
          />
        )}

        <CardContent
          sx={{
            p: { xs: 2, sm: 3 },
            "@media (max-width: 1000px) and (max-height: 600px)": {
              p: 1.5,
            },
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={{ xs: 1.5, sm: 2 }}
            sx={{
              "@media (max-width: 1000px) and (max-height: 600px)": {
                mb: 1,
              },
            }}
          >
            <Tooltip title="Go back to job selection">
              <IconButton
                onClick={backToJobSelection}
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  color: theme.palette.error.main,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) },
                  "@media (max-width: 1000px) and (max-height: 600px)": {
                    width: 32,
                    height: 32,
                  },
                }}
                disabled={isLoading}
              >
                <ArrowBackIcon
                  sx={{
                    "@media (max-width: 1000px) and (max-height: 600px)": {
                      fontSize: "0.9rem",
                    },
                  }}
                />
              </IconButton>
            </Tooltip>

            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                textFillColor: "transparent",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "1.3rem", sm: "1.7rem" },
                "@media (max-width: 1000px) and (max-height: 600px)": {
                  fontSize: "1.2rem",
                },
              }}
            >
              Manual Job Entry
            </Typography>

            <Box />
          </Stack>

          {/* Employee Info */}
          {employeeName && (
            <Stack
              direction="row"
              spacing={1}
              mb={{ xs: 1.5, sm: 2 }}
              justifyContent="center"
              sx={{
                "@media (max-width: 1000px) and (max-height: 600px)": {
                  mb: 1,
                },
              }}
            >
              <Chip
                icon={<PersonIcon />}
                label={`Employee: ${employeeName}`}
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "0.75rem", sm: "0.8rem" },
                  height: { xs: 26, sm: 30 },
                  "@media (max-width: 1000px) and (max-height: 600px)": {
                    fontSize: "0.65rem",
                    height: 24,
                  },
                }}
              />
            </Stack>
          )}

          {/* Form Fields */}
          <Box
            sx={{
              "@media (max-width: 1000px) and (max-height: 600px)": {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.5,
                "& > *:last-child": {
                  gridColumn: "1 / -1",
                },
              },
            }}
          >
            {formFields.map((field, index) => (
              <Fade in={true} timeout={300 + index * 100} key={field.id}>
                <TextField
                  id={field.id}
                  label={field.label}
                  placeholder={field.placeholder}
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={field.value}
                  onChange={
                    field.isJobNumber
                      ? handleJobNumberChange
                      : (e) => {
                          field.setValue(e.target.value);
                          validateField(field.id, e.target.value);
                        }
                  }
                  error={!!errors[field.id]}
                  helperText={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span
                        style={{
                          color: errors[field.id]
                            ? theme.palette.error.main
                            : "transparent",
                        }}
                      >
                        {errors[field.id] ||
                          (field.isJobNumber
                            ? "Format: NN-NN-XXXX (e.g., 11-22-1234)"
                            : " ")}
                      </span>
                      <span
                        style={{
                          color: theme.palette.text.secondary,
                          marginLeft: "auto",
                        }}
                      >
                        {field.value.length}/{field.maxLength}
                      </span>
                    </Box>
                  }
                  inputProps={{ maxLength: field.maxLength }}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ color: "action.active" }}>
                        {field.icon}
                      </InputAdornment>
                    ),
                    endAdornment:
                      field.isJobNumber && field.value ? (
                        <Tooltip title="Clear job number">
                          <IconButton
                            aria-label="clear job number"
                            onClick={clearJobNumber}
                            edge="end"
                            size="small"
                            disabled={isLoading}
                            sx={{
                              color: "action.active",
                              "&:hover": {
                                color: theme.palette.error.main,
                                backgroundColor: alpha(
                                  theme.palette.error.main,
                                  0.08
                                ),
                              },
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: { xs: "0.85rem", sm: "0.95rem" },
                      backgroundColor: alpha(theme.palette.background.paper, 0.98),
                      "& fieldset": {
                        borderColor: alpha(theme.palette.divider, 0.6),
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                      "@media (max-width: 1000px) and (max-height: 600px)": {
                        fontSize: "0.75rem",
                        "& .MuiInputBase-input": {
                          padding: "5px 8px",
                        },
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      width: "100%",
                      marginTop: "4px",
                      fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      "@media (max-width: 1000px) and (max-height: 600px)": {
                        fontSize: "0.6rem",
                        marginTop: "2px",
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: { xs: "0.85rem", sm: "0.95rem" },
                      "@media (max-width: 1000px) and (max-height: 600px)": {
                        fontSize: "0.75rem",
                      },
                    },
                  }}
                />
              </Fade>
            ))}

            {/* Submit Button */}
            <Fade in={true} timeout={800}>
              <Button
                variant="contained"
                size="medium"
                fullWidth
                onClick={handleConfirm}
                disabled={isLoading}
                startIcon={isLoading ? null : <CheckCircleIcon />}
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  py: { xs: 1, sm: 1.2 },
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    boxShadow: `0 14px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    background: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                  transition: "all 0.2s ease-in-out",
                  "@media (max-width: 1000px) and (max-height: 600px)": {
                    mt: 1,
                    py: 0.7,
                    fontSize: "0.8rem",
                    "& .MuiButton-startIcon": {
                      marginRight: "4px",
                      "& svg": {
                        fontSize: "0.8rem",
                      },
                    },
                  },
                }}
              >
                {isLoading ? "Starting Job..." : "Confirm & Start Job"}
              </Button>
            </Fade>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
